import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaWhatsapp, FaCheck, FaTrash, FaArrowRight, FaFileExcel, FaEnvelope } from 'react-icons/fa';
import { FcCancel } from 'react-icons/fc';
import { MdMailOutline } from 'react-icons/md';
import { IoIosRefresh } from 'react-icons/io';
import { exportToExcel } from '../../../services/ExcelExportService';

import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import MenuFilter from '../../atoms/MenuFilter';
import SimpleCalendar from '../../atoms/SimpleCalendar';
import StatusFilter from '../../atoms/StatusFilter';
import PageSearch from '../../molecules/PageSearch';
import TableSkeleton from '../../atoms/TableSkeleton';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import Loading from '../../ions/Loading';
import NotificationPageView from './NotificationPageView';
import MessageList from './MessageList';
import MessageDetailsComponent from './MessageDetails';
import { extractAndConvertFrom } from '../../../utils/DateUtils';
import { DEFAULT_SCHEDULED_TYPE } from '../../../utils/constants';
import { RiMailSendFill } from 'react-icons/ri';

type Notification = {
    type_scheduled: string;
    operation: string;
    updatedAt: string;
    maxAttempts: number;
    attempts: number;
    media: string;
    status: string;
    createdAt: string;
    sentAt: string;
    contactName: string;
    phone: string;
    isPhoneValid: boolean;
    email: string;
    isEmailValid: boolean;
    canDispatch: boolean;
    canEdit: boolean;
    canDelete: boolean;
    errorMessage: string;
    totalMessages: number;
    sentMessages: number;
    deliveredMessages: number;
};

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = process.env.REACT_APP_COMMUNICATION_API_URL;

const WHATSAPP = 'WHATSAPP';

const NotificationPageList = () => {
    const navigate = useNavigate();
    const { filter }: any = useParams();

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [totalElements, setTotalElements] = useState(0);
    const [data, setData] = useState<any>(null);
    const [showForm, setShowForm] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showMessages, setShowMessages] = useState(false);
    const [showMessageDetails, setShowMessageDetails] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [removingItems, setRemovingItems] = useState<string[]>([]);
    const [processingItems, setProcessingItems] = useState<string[]>([]);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [isLoadingAction, setIsLoadingAction] = useState(false);

    const [templates, setTemplates] = useState<any[]>([]);
    const [apiInfo, setApiInfo] = useState({ name: 'Communication API', version: '1.0.0', environment: 'development' });
    const exportRows = useRef<Notification[]>([]);

    const TODAY = new Date().toISOString().split('T')[0];

    let txtFullName = '';
    let txtScheduledType = '';
    let txtTargetDate = '';

    if (filter) {
        const params = filter.split(';');
        params.forEach((param: string) => {
            if (param.startsWith('f=')) txtFullName = param.substring(2);
            if (param.startsWith('s=')) txtScheduledType = param.substring(2);
            if (param.startsWith('d=')) txtTargetDate = param.substring(2);
        });
    }

    if (!txtScheduledType) txtScheduledType = '';
    if (!txtTargetDate) txtTargetDate = TODAY;

    const [searchValue, setSearchValue] = useState(txtFullName);
    const [selectedScheduledType, setSelectedScheduledType] = useState(txtScheduledType);
    const [selectedTargetDate, setSelectedTargetDate] = useState(txtTargetDate);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const url = `${BASE_URL}/notifications/page-list?search=${searchValue}&notification_type=${selectedScheduledType}&target_date=${selectedTargetDate}`;
            const token = localStorage.getItem('authToken');
            const response = await fetch(url, {
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const responseData = await response.json();
                setData(responseData);
                setNotifications(responseData.list || []);
                setTotalElements(responseData.totalElements || 0);
                exportRows.current = responseData.list || [];

                // Atualizar templates se disponível
                if (responseData.templates) {
                    setTemplates(responseData.templates);
                }

                // Atualizar dados da API
                setApiInfo({
                    name: responseData.apiName || 'Communication',
                    version: responseData.apiVersion || '1.0.0',
                    environment: responseData.environment
                });
            } else {
                toast.error('Erro ao carregar notificações');
            }
        } catch (error) {
            toast.error('Erro ao carregar notificações');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, [searchValue, selectedScheduledType, selectedTargetDate]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            txtContactNameChanged(searchValue);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchValue]);

    const urlNavigate = (
        fullName: string,
        scheduledType: string,
        targetDate: string
    ) => {
        return `/communication/notifications/f=${fullName};s=${scheduledType};d=${targetDate};`;
    };

    const filterScheduledTypeChange = (value: string) => {
        setSelectedScheduledType(value);
        navigate(urlNavigate(searchValue, value, selectedTargetDate), {
            replace: true,
        });
    };

    const filterTargetDateChange = (date: Date) => {
        // Formatar data no timezone local para evitar problemas de fuso horário
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        setSelectedTargetDate(formattedDate);
        navigate(urlNavigate(searchValue, selectedScheduledType, formattedDate), {
            replace: true,
        });
    };

    const txtContactNameChanged = (description: string) => {
        navigate(urlNavigate(description, selectedScheduledType, selectedTargetDate), {
            replace: true,
        });
    };

    const onFilterClean = () => {
        const today = new Date().toISOString().split('T')[0];
        setSearchValue('');
        setSelectedScheduledType(DEFAULT_SCHEDULED_TYPE);
        setSelectedTargetDate(today);
        navigate(urlNavigate('', DEFAULT_SCHEDULED_TYPE, today), {
            replace: true,
        });
    };

    const getMediaIcon = (media: string) => {
        return media === WHATSAPP ?
            <FaWhatsapp className="text-green-500" /> :
            <MdMailOutline className="text-blue-500" />;
    };

    const getStatusColor = (status: string, operation?: string) => {
        // Se está sendo processado, mostrar cor laranja
        if (operation && processingItems.includes(operation)) {
            return 'bg-orange-500 animate-pulse';
        }
        
        const colors = {
            'PENDING': 'bg-slate-500',
            'CHECKING': 'bg-slate-600',
            'SENDING': 'bg-blue-500',
            'SENT': 'bg-green-600',
            'DELIVERED': 'bg-green-700',
            'RECEIVED': 'bg-blue-600',
            'ERROR': 'bg-red-600',
            'FAILED': 'bg-red-700',
            'CANCELLED': 'bg-orange-600',
        };
        return colors[status as keyof typeof colors] || 'bg-gray-500';
    };

    const getStatusLabel = (status: string) => {
        const labels: { [key: string]: string } = {
            'PENDING': 'Pendente',
            'CHECKING': 'Verificando',
            'SENDING': 'Enviando',
            'SENT': 'Enviado',
            'DELIVERED': 'Entregue',
            'RECEIVED': 'Recebido',
            'ERROR': 'Erro',
            'FAILED': 'Falhou',
            'CANCELLED': 'Cancelado',
            'CONSUMED': 'Consumido'
        };
        return labels[status] || status;
    };

    const statusOptions = useMemo(() => {
        if (data && data.status && Array.isArray(data.status)) {
            return data.status.map((item: any) => ({
                value: item.status,
                label: getStatusLabel(item.status),
                color: item.status,
                count: item.count
            }));
        }
        return [
            { value: 'PENDING', label: 'Pendente', color: 'PENDING' },
            { value: 'CHECKING', label: 'Verificando', color: 'CHECKING' },
            { value: 'SENT', label: 'Enviado', color: 'SENT' },
            { value: 'DELIVERED', label: 'Entregue', color: 'DELIVERED' },
            { value: 'RECEIVED', label: 'Recebido', color: 'RECEIVED' },
            { value: 'ERROR', label: 'Erro', color: 'ERROR' },
            { value: 'FAILED', label: 'Falhou', color: 'FAILED' },
            { value: 'CANCELLED', label: 'Cancelado', color: 'CANCELLED' },
        ];
    }, [data]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedItems(notifications.filter(n => n.canDispatch).map(n => n.operation));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (operation: string) => {
        const notification = notifications.find(n => n.operation === operation);
        if (!notification?.canDispatch) return;

        setSelectedItems(prev =>
            prev.includes(operation)
                ? prev.filter(id => id !== operation)
                : [...prev, operation]
        );
    };

    const filterGroups = [
        {
            title: '',
            children: (
                <SimpleCalendar
                    selectedDate={new Date(selectedTargetDate + 'T12:00:00')}
                    onDateChange={filterTargetDateChange}
                />
            )
        },
        {
            title: 'Tipo de Notificação',
            children: (
                <div className="space-y-1">
                    <MenuFilter
                        title=""
                        options={templates.map((template: any) => ({
                            value: template.id,
                            label: template.description,
                            icon: template.type === 'WPP' ? 'whatsapp' : 'email'
                        }))}
                        value={selectedScheduledType}
                        onChange={filterScheduledTypeChange}
                    />
                </div>
            )
        },
        {
            title: '',
            children: (
                <StatusFilter
                    options={statusOptions}
                    selectedStatuses={selectedStatuses}
                    onChange={setSelectedStatuses}
                />
            )
        }
    ];

    const handleRowClick = (notification: Notification) => {
        setSelectedNotification(notification);
        setShowDetails(true);
        setShowForm(false);
        setShowMessages(false);
        setShowMessageDetails(false);
    };

    const handleMessagesClick = (notification: Notification, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedNotification(notification);
        setShowMessages(true);
        setShowDetails(false);
        setShowForm(false);
        setShowMessageDetails(false);
    };

    const handleMessageSelect = (messageId: string) => {
        setSelectedMessageId(messageId);
        setShowMessageDetails(true);
        setShowMessages(false);
    };

    const handleBackToMessages = () => {
        setShowMessages(true);
        setShowMessageDetails(false);
        setSelectedMessageId(null);
    };

    const handleCloseRightPanel = () => {
        setShowDetails(false);
        setShowMessages(false);
        setShowMessageDetails(false);
        setSelectedNotification(null);
        setSelectedMessageId(null);
    };

    const handleExport = () => {
        const headers = [
            { id: 'operation', title: 'Operação', field: 'operation', canSort: false },
            { id: 'contactName', title: 'Nome do contato', field: 'contactName', canSort: false },
            { id: 'media', title: 'Mídia', field: 'media', canSort: false },
            { id: 'phone', title: 'Telefone', field: 'phone', canSort: false },
            { id: 'email', title: 'Email', field: 'email', canSort: false },
            { id: 'status', title: 'Status', field: 'status', canSort: false },
            { id: 'attempts', title: 'Tentativas', field: 'attempts', canSort: false },
            { id: 'sentAt', title: 'Enviado em', field: 'sentAt', canSort: false }
        ];
        exportToExcel(headers, exportRows.current, `notificacoes_${selectedTargetDate}.csv`);
    };

    const forceReload = () => {
        loadNotifications();
    };

    const dispatch = async (service: string) => {
        setIsLoadingAction(true);
        let textMessage = 'Notificação iniciada com sucesso!';
        if (service === 'WPP-STATUS') {
            textMessage = 'Status das notificações checado com sucesso!';
        }

        // Armazenar operations que serão processadas
        const operationsToProcess = [...selectedItems];
        
        // Adicionar efeito visual de processamento
        if (service !== 'WPP-STATUS') {
            setProcessingItems(operationsToProcess);
        }

        try {
            const response = await fetch(`${BASE_URL}/dispatch`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    notificationType: selectedScheduledType,
                    targetDate: selectedTargetDate,
                    service,
                    operations: selectedItems,
                }),
            });

            if (response.ok) {
                toast.success(textMessage);
                
                // Atualizar status localmente sem reload
                if (service !== 'WPP-STATUS') {
                    // Aguardar um pouco para o efeito visual
                    setTimeout(() => {
                        setNotifications(prev => prev.map(notification => {
                            if (operationsToProcess.includes(notification.operation)) {
                                return {
                                    ...notification,
                                    status: 'SENDING',
                                    canEdit: false,
                                    canDelete: false,
                                    canDispatch: false,
                                    attempts: notification.attempts + 1,
                                    updatedAt: new Date().toISOString()
                                };
                            }
                            return notification;
                        }));
                        
                        // Remover efeito de processamento
                        setProcessingItems([]);
                    }, 800);
                }
                
                setSelectedItems([]);
            } else {
                toast.error('Erro ao iniciar notificação!');
                setProcessingItems([]);
            }
        } catch (error) {
            toast.error('Ocorreu um erro na comunicação com o servidor!');
            setProcessingItems([]);
        }
        setIsLoadingAction(false);
    };

    const removeNotification = async (operation: string) => {
        if (window.confirm('Deseja realmente remover essa notificação?')) {
            setIsLoadingAction(true);
            setRemovingItems([...removingItems, operation]);

            try {
                const response = await fetch(`${BASE_URL}/notifications/${selectedScheduledType}:${selectedTargetDate}:${operation}`, {
                    method: 'DELETE',
                    headers: {
                        'x-api-key': API_KEY || '',
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    toast.success('Notificação removida com sucesso!');

                    // Aguardar transição antes de remover
                    setTimeout(() => {
                        setSelectedItems(selectedItems.filter(item => item !== operation));
                        setNotifications(notifications.filter(n => n.operation !== operation));
                        setTotalElements(totalElements - 1);
                        setRemovingItems(removingItems.filter(item => item !== operation));
                    }, 300);
                } else {
                    toast.error('Erro ao remover notificação!');
                    setRemovingItems(removingItems.filter(item => item !== operation));
                }
            } catch (error) {
                toast.error('Erro ao remover notificação!');
                setRemovingItems(removingItems.filter(item => item !== operation));
            }
            setIsLoadingAction(false);
        }
    };

    return (
        <>
            {initialLoading && <Loading />}

            {!showDetails && !showMessages && !showMessageDetails && data?.canCreateNew && (
                <button
                    onClick={() => navigate(`/communication/batch-form/${selectedScheduledType}/${selectedTargetDate}`)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova notificação"
                >
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <RiMailSendFill className="text-blue-600" size={18} />
                            <span>Comunicação</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            <div>v{apiInfo.version}</div>
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || 'dev').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar
                        filterGroups={initialLoading ? [] : filterGroups}
                        showFooter={true}
                        footerData={{
                            totalRecords: totalElements,
                            apiInfo: apiInfo
                        }}
                    >
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showDetails || showMessages || showMessageDetails}
                onCloseRightPanel={handleCloseRightPanel}
                rightPanel={
                    showMessageDetails && selectedMessageId ? (
                        <div className="h-full">
                            <MessageDetailsComponent
                                messageId={selectedMessageId}
                                onBack={handleBackToMessages}
                            />
                        </div>
                    ) : showMessages && selectedNotification ? (
                        <div className="h-full">
                            <MessageList
                                notificationId={(selectedNotification as any).compositeId || selectedNotification.operation}
                                onClose={handleCloseRightPanel}
                                onMessageSelect={handleMessageSelect}
                            />
                        </div>
                    ) : showDetails && selectedNotification ? (
                        <div className="h-full">
                            <NotificationPageView
                                typeScheduled={selectedScheduledType}
                                targetDate={selectedTargetDate}
                                operation={selectedNotification.operation}
                                onClose={() => setShowDetails(false)}
                            />
                        </div>
                    ) : null
                }
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Fixed Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Notificações
                                </h1>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {totalElements} registros encontrados
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={forceReload}
                                    disabled={isLoadingAction}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista"
                                >
                                    <IoIosRefresh size={18} />
                                </button>

                                <button
                                    onClick={handleExport}
                                    className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    title="Exportar para Excel"
                                >
                                    <FaFileExcel className="mr-1" size={14} />
                                    Exportar
                                </button>

                                {selectedItems.length > 0 && (
                                    <button
                                        onClick={() => dispatch('')}
                                        disabled={isLoadingAction}
                                        className="flex items-center px-3 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                    >
                                        <span className="bg-orange-700 text-xs px-1.5 py-0.5 rounded-full mr-2">
                                            {selectedItems.length}
                                        </span>
                                        Notificar
                                        <FaArrowRight className="ml-1" size={12} />
                                    </button>
                                )}

                                {notifications.filter(n => ['SENT', 'RECEIVED', 'DELIVERED'].includes(n.status) && selectedScheduledType === DEFAULT_SCHEDULED_TYPE).length > 0 && (
                                    <button
                                        onClick={() => dispatch('WPP-STATUS')}
                                        disabled={isLoadingAction}
                                        className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <span className="bg-green-800 text-xs px-1.5 py-0.5 rounded-full mr-2">
                                            {notifications.filter(n => ['SENT', 'RECEIVED', 'DELIVERED'].includes(n.status)).length}
                                        </span>
                                        Checar status
                                    </button>
                                )}

                                <div className="w-80">
                                    <PageSearch
                                        value={searchValue}
                                        label="Buscar contato"
                                        onSearch={txtContactNameChanged}
                                        filterCleanVisible={false}
                                        filterCleanEnabled={Boolean(searchValue)}
                                        onFilterClean={onFilterClean}
                                        configureVisible={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        {loading ? (
                            <div className="flex-1 overflow-y-auto">
                                <TableSkeleton rows={8} />
                            </div>
                        ) : (
                            <>
                                <div className="flex-shrink-0 bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="text-xs">
                                                <th className="w-12 px-3 py-2 text-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.length === notifications.filter(n => n.canDispatch).length && notifications.filter(n => n.canDispatch).length > 0}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                </th>
                                                <th className="w-12 px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Tipo
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Contato
                                                </th>
                                                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Detalhes
                                                </th>
                                                <th className="w-24 px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Mensagens
                                                </th>
                                                <th className="w-32 px-3 py-2 text-left text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Enviado
                                                </th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>

                                <div className="flex-1 overflow-y-auto">
                                    <table className="min-w-full">
                                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                            {notifications.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                                        Nenhuma notificação encontrada
                                                    </td>
                                                </tr>
                                            ) : (
                                                notifications.map((notification, index) => (
                                                    <tr
                                                        key={index}
                                                        className={`text-sm relative group transition-all duration-500 cursor-pointer ${
                                                            removingItems.includes(notification.operation)
                                                                ? 'opacity-0 transform scale-95 bg-red-50 dark:bg-red-900/20'
                                                                : processingItems.includes(notification.operation)
                                                                    ? 'bg-orange-50 dark:bg-orange-900/20 animate-pulse border-l-4 border-orange-400'
                                                                    : selectedItems.includes(notification.operation)
                                                                        ? 'bg-blue-50 dark:bg-blue-900/20'
                                                                        : 'hover:bg-gray-50 dark:hover:bg-slate-700 hover:border hover:border-gray-300 dark:hover:border-slate-600'
                                                        }`}
                                                        onMouseEnter={() => setHoveredRow(index)}
                                                        onMouseLeave={() => setHoveredRow(null)}
                                                        onClick={() => handleRowClick(notification)}
                                                    >
                                                        <td className="w-12 px-3 py-2">
                                                            {notification.canDispatch && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedItems.includes(notification.operation)}
                                                                    onChange={() => handleSelectItem(notification.operation)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="w-12 px-3 py-2">
                                                            {getMediaIcon(notification.media)}
                                                        </td>
                                                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                                                            {notification.contactName}
                                                        </td>
                                                        <td className="px-3 py-2 relative">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                                                        {notification.media === WHATSAPP ? notification.phone : notification.email}
                                                                    </span>
                                                                    {notification.media === WHATSAPP ? (
                                                                        notification.isPhoneValid ? (
                                                                            <FaCheck className="text-green-500" size={12} />
                                                                        ) : (
                                                                            <FcCancel size={14} />
                                                                        )
                                                                    ) : (
                                                                        notification.isEmailValid ? (
                                                                            <FaCheck className="text-green-500" size={12} />
                                                                        ) : (
                                                                            <FcCancel size={14} />
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(notification.status, notification.operation)}`}>
                                                                        {processingItems.includes(notification.operation) ? 'PROCESSANDO...' : notification.status}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="w-24 px-3 py-2">
                                                            {notification.totalMessages > 0 ? (
                                                                <button
                                                                    onClick={(e) => handleMessagesClick(notification, e)}
                                                                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded-lg transition-colors"
                                                                    title="Ver mensagens"
                                                                >
                                                                    <FaEnvelope size={12} />
                                                                    <span className="text-xs font-medium">
                                                                        {notification.sentMessages}/{notification.totalMessages}
                                                                    </span>
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                                                            )}
                                                        </td>
                                                        <td className="w-32 px-3 py-2 text-xs text-gray-900 dark:text-white relative">
                                                            <div className={`transition-opacity duration-200 ${hoveredRow === index && notification.status === 'PENDING' && notification.canDelete ? 'opacity-0' : 'opacity-100'}`}>
                                                                {notification.sentAt ? extractAndConvertFrom(notification.sentAt) : '-'}
                                                            </div>

                                                            {hoveredRow === index && notification.status === 'PENDING' && notification.canDelete && (
                                                                <div className="absolute inset-0 flex items-center justify-center space-x-2 bg-transparent dark:bg-slate-800">
                                                                    <button
                                                                        className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeNotification(notification.operation);
                                                                        }}
                                                                        title="Excluir notificação"
                                                                    >
                                                                        <FaTrash size={16} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </ThreeColumnLayout>
        </>
    );
};

export default NotificationPageList;
